// src/lib/authRecovery.ts
// Simplified session recovery — single step via /auth { method: 'refresh' }
// Replaces supabaseSessionRecovery.ts (178 lines → ~40 lines)

import { callAuth } from '@/lib/authClient'

function getErrorMessage(error: unknown): string {
  if (!error) return ''
  if (typeof error === 'string') return error
  if (typeof error === 'object' && 'message' in error && typeof (error as any).message === 'string') {
    return (error as any).message
  }
  return String(error)
}

export function isJwtExpiredError(error: unknown): boolean {
  const message = getErrorMessage(error).toLowerCase()
  if (message.includes('jwt expired')) return true
  if (message.includes('invalid jwt')) return true
  const status = typeof error === 'object' && error && 'status' in (error as any)
    ? Number((error as any).status)
    : null
  return status === 401
}

let activeRecovery: Promise<boolean> | null = null

async function refreshAuthToken(): Promise<boolean> {
  const token = localStorage.getItem('auth_token')
  if (!token) return false

  try {
    const result = await callAuth('refresh', { currentToken: token })
    localStorage.setItem('auth_token', result.token)
    return true
  } catch {
    return false
  }
}

export async function withAuthRecovery<TResponse extends { data: any; error: any }>(
  call: () => PromiseLike<TResponse>
): Promise<TResponse> {
  let result = await call()
  if (!result.error) return result
  if (!isJwtExpiredError(result.error)) return result

  // Deduplicate concurrent recovery attempts
  if (!activeRecovery) {
    activeRecovery = refreshAuthToken().finally(() => { activeRecovery = null })
  }

  const recovered = await activeRecovery
  if (!recovered) return result

  return call()
}
